import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { SyncService, ZoningCodeService } from 'src/app/core/services';
import { AuthenticationService } from 'src/shared/auth';
import { ProductFeatures } from 'src/shared/auth/enums/product-features-enum';
import { ConfirmationDialogComponent } from 'src/shared/zoneiqui/components';
import notificationContentDictionary from '../../models/tables/dictionary/notificationsContent.dictionary';
import { ToastrService } from 'ngx-toastr';
import { showValidateButton } from 'src/app/app-settings';

@Component({
    standalone: false,
    selector: 'ziq-footer',
    templateUrl: './ziq-footer.component.html',
    styleUrls: ['./ziq-footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZIQFooterComponent implements OnInit, OnDestroy {
    @Input()
    entityTab: string;

    @Input()
    calibrationTab: string;

    showValidateButton: boolean = showValidateButton;
    isPublishing: boolean = false;
    readonly canShowPublishButton$: Observable<boolean>;
    readonly getCanExportToXSLX$: Observable<boolean>;

    private readonly subscription = new Subscription();

    constructor(
        private readonly zcService: ZoningCodeService,
        private readonly toastrService: ToastrService,
        private readonly dialog: MatDialog,
        private readonly authenticationService: AuthenticationService,
        private readonly syncService: SyncService,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.canShowPublishButton$ = this.authenticationService
            .checkIfFeatureGranted(ProductFeatures.ECI_PUBLISH_DRAFT)
            .pipe(startWith(false));

        this.getCanExportToXSLX$ = this.authenticationService
            .checkIfFeatureGranted(ProductFeatures.ECI_EXPORT_ZC_TO_XLSX)
            .pipe(startWith(false));
    }

    ngOnInit(): void {
        this.subscription.add(
            this.syncService.changeDetection.subscribe(() => {
                this.cdr.markForCheck();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    get isZoningCodeChanged() {
        return this.zcService.isZoningCodeChanged;
    }

    get statusText() {
        return this.isZoningCodeChanged ? 'Modified' : 'Saved';
    }

    get isZoningCodeValid() {
        return this.zcService.isZoningCodeValid;
    }

    get isZCValidWithoutRequiredFields() {
        return this.zcService.isZCValidWithoutRequiredFields;
    }

    get canPublish() {
        return (
            !this.isPublishing && this.zcService.hasZoningCode && !this.isZoningCodeChanged && this.isZoningCodeValid
        );
    }

    get showImportButton() {
        return ['zones'].includes(this.entityTab) && ['general', 'uses', 'use-capacity'].includes(this.calibrationTab);
    }

    publishDraft() {
        if (this.isPublishing) {
            // console.log(`publishDraft _isPublishing: ${this.isPublishing}`);
            return;
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            title: 'Publish Zoning Code Draft',
            topContent: `This operation can't be undone. All changed from Draft will be applied to original Zoning Code.
      Continue?`
        };

        const publishDialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
        publishDialogRef.afterClosed().subscribe(startPublishing => {
            if (!startPublishing) {
                return;
            }
            this.isPublishing = true;
            this.cdr.markForCheck();
            this.zcService
                .publishDraft()
                .pipe(
                    switchMap(result => {
                        console.log(`Zoning Code Draft successfully published, time: ${_.get(result, 'time')}`);
                        return this.zcService.updateDraft({
                            IsPublishingRequired: false
                        });
                    }),
                    catchError(err => {
                        this.isPublishing = false;
                        this.cdr.markForCheck();
                        this.toastrService.error(
                            `${notificationContentDictionary.publishingError.label} ${_.get(err, 'error.messages[0]')}`,
                            'Error'
                        );
                        return of(null);
                    })
                )
                .subscribe(result => {
                    this.isPublishing = false;
                    this.cdr.markForCheck();
                    if (result !== null) {
                        this.toastrService.success(notificationContentDictionary.publishingSuccess.label, 'Success');
                    }
                });
        });
    }

    /**
     * TODO :: describe why overall validation happens after data is saved
     * Running this method triggers change detection for all fields with this.syncService.triggerChangeDetection();
     * The process is like that:
     * 1. ECI sends data to save to Calibration.
     * 2. Calibration processed data and sends back an event by WebSockets.
     * 3. Changes applied to Zoning Code
     * 4. Change detection for all fields are triggered.
     */
    saveChangedData() {
        this.zcService
            .saveChangedData()
            .pipe(
                switchMap(() => {
                    return this.zcService.updateDraft({
                        IsPublishingRequired: true
                    });
                })
            )
            .subscribe();
    }

    cancelChanges() {
        this.zcService.cancelZCChanges();
    }

    validateZC() {
        console.log(this.zcService.selectedZoningCode.getValidationResults(false));
    }

    openImportUsesPage() {
        if (this.zcService.selectedZoningCode?.IsPublishingRequired) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.autoFocus = true;
            dialogConfig.data = {
                title: 'Publish Draft Code Required',
                topContent: 'ATTENTION: The active draft must be published before Uses can be imported.',
                yesButtonLabel: 'OK',
                hideNoButton: true
            };

            this.dialog.open(ConfirmationDialogComponent, dialogConfig);
            return;
        }

        this.zcService
            .getZCImportUrl(this.zcService.originalZoningCodeId, this.entityTab, this.calibrationTab)
            .subscribe(url => {
                window.open(url, '_blank');
            });
    }

    getImportButtonLabel() {
        switch (this.calibrationTab) {
            case 'general':
                return 'Import';
            case 'uses':
                return 'Import Uses';
            default:
                return 'Import';
        }
    }
}
